/*
 * EnterL2Card — the "needs deep scoping" recommendation (compact redesign).
 *
 * Fires when the problem gate verdict is solution_partner (unclear / heavy-
 * regulated / done-for-me). It's a decision moment, not a form, so it stays
 * tight: a sharp headline, the WHY clamped behind "See full" (scrolls when
 * long), the three phases of the walk in one glance, then the fork: do it
 * yourself, book a call with a human Solution Partner, or draft the PRD now.
 * All three are open and free. The pay-gate is retired, and so is the phrase
 * "deep scoping": nobody is blocked, and no copy implies a charge.
 *
 * The walk always runs in three phases (Phase 1/2/3, phase_state.py), so
 * they're authored statically here rather than dumped from the section
 * registry (the old H/I/M/N/O + role list read as noise).
 *
 * Brand: light #eef3ff surface, IBM Plex, Plex Mono eyebrow in brand blue,
 * solid-blue primary CTA, ghost secondary. No emoji, sentence case.
 */

import React from 'react';
import { ArrowRight, FileText, Users } from 'lucide-react';

import type { DirectivePayload } from '../../lib/dossier-types';


export interface EnterL2CardProps {
  payload: DirectivePayload;
  onContinue?: () => void;
  /** Receives the payload so the slot picker names the partner the backend
   *  put on this card, rather than a hardcoded stand-in. */
  onTalkToPartner?: (payload: DirectivePayload) => void;
  /** "Draft PRD": the same draft action the propose_prd_draft card uses. */
  onCreatePrd?: () => void;
  disabled?: boolean;
}

const PHASES: Array<{ n: number; label: string; detail: string }> = [
  { n: 1, label: 'Company & context', detail: 'Strategy, tools, budget, approvals, compliance.' },
  { n: 2, label: 'The problem, in depth', detail: 'Workflows, data sources, the edge cases.' },
  { n: 3, label: 'Outcomes & PRD', detail: 'Metrics, milestones, a build-ready spec.' },
];

export const EnterL2Card: React.FC<EnterL2CardProps> = ({
  payload,
  onContinue,
  onTalkToPartner,
  onCreatePrd,
  disabled,
}) => {
  const reason = (payload.reason as string | undefined)
    || (payload.lead as string | undefined)
    || '';
  const primaryLabel = (payload.primary_cta as string | undefined) || 'Do it yourself';
  const secondaryLabel = (payload.secondary_cta as string | undefined)
    || 'Book a call with a human Solution Partner';
  const tertiaryLabel = (payload.tertiary_cta as string | undefined) || 'Draft PRD';
  const partnerName = (payload.partner_name as string | undefined)
    || 'A Solution partner from our team';

  // Compliance Orbit inferred from the PROBLEM (not what the user picked) — the
  // reason this needs deep scoping in the first place. The backend computes and
  // sends these (inferred_frameworks + compliance_note); the card used to throw
  // them away. Now we name them so the user sees WHY ("this handles patient data
  // → HIPAA applies") and can push back.
  const inferredFrameworks = (payload.inferred_frameworks as string[] | undefined) || [];
  const complianceNote = ((payload.compliance_note as string | undefined) || '').trim();

  return (
    <div className="aa-deep-scope">
      <header className="aa-deep-scope-head">
        <span className="aa-deep-scope-eyebrow">How do you want to take this forward?</span>
        <h3 className="aa-deep-scope-title">Let's scope this properly before we build.</h3>
      </header>

      {reason && (
        <div className="aa-deep-scope-reason-wrap">
          <p className="aa-deep-scope-reason">
            {reason}
          </p>
        </div>
      )}

      {inferredFrameworks.length > 0 && (
        <div className="aa-deep-scope-compliance">
          <div className="aa-deep-scope-compliance-head">
            <span className="aa-deep-scope-compliance-label">Compliance flagged</span>
            <div className="aa-deep-scope-compliance-chips">
              {inferredFrameworks.map((fw) => (
                <span key={fw} className="aa-deep-scope-compliance-chip">{fw.toUpperCase()}</span>
              ))}
            </div>
          </div>
          {complianceNote && (
            <p className="aa-deep-scope-compliance-note">{complianceNote}</p>
          )}
        </div>
      )}

      <ol className="aa-deep-scope-phases" role="list">
        {PHASES.map((p) => (
          <li key={p.n} className="aa-deep-scope-phase">
            <span className="aa-deep-scope-phase-num">{p.n}</span>
            <span className="aa-deep-scope-phase-label">{p.label}</span>
            <span className="aa-deep-scope-phase-detail">{p.detail}</span>
          </li>
        ))}
      </ol>

      <p className="aa-deep-scope-fork-note">
        Do it yourself and I'll walk the three phases with you. Want expert
        help instead? {partnerName} can take it from here. Or take the brief
        now and draft the PRD from what we have.
      </p>

      <footer className="aa-deep-scope-actions">
        <button
          type="button"
          className="aa-deep-scope-primary"
          onClick={onContinue}
          disabled={disabled || !onContinue}
        >
          <span>{primaryLabel}</span>
          <ArrowRight size={14} strokeWidth={2.5} />
        </button>
        <button
          type="button"
          className="aa-deep-scope-secondary"
          onClick={() => onTalkToPartner?.(payload)}
          disabled={disabled || !onTalkToPartner}
        >
          <Users size={13} strokeWidth={2.25} />
          <span>{secondaryLabel}</span>
        </button>
        <button
          type="button"
          className="aa-deep-scope-secondary"
          onClick={onCreatePrd}
          disabled={disabled || !onCreatePrd}
        >
          <FileText size={13} strokeWidth={2.25} />
          <span>{tertiaryLabel}</span>
        </button>
      </footer>
    </div>
  );
};

export default EnterL2Card;
